// AlertQueue.jsx
// Subscribes to unconsumed alerts and shows them one at a time. After each
// AlertToast completes its lifecycle, marks the alert consumed and advances
// to the next queued alert.
//
// Key design: incoming alerts are snapshotted into a local display queue the
// moment they appear. This makes the queue immune to TTL expiry — once an
// alert is queued for display, it WILL display regardless of whether the
// Firestore doc's expiresAt passes before its turn comes. The TTL is purely
// a server-side cleanup mechanism; the consumed flag prevents re-display.
//
// Mounted by /overlay/alerts (the always-on browser source layered on every
// OBS scene). Reads templates from config/alertTemplates with fallback to
// the seed values in DEFAULT_ALERT_TEMPLATES.
import { useEffect, useRef, useState } from "react";
import { doc, onSnapshot, updateDoc } from "firebase/firestore";
import { db } from "./../../firebase";
import useAlerts from "./../../hooks/useAlerts";
import useStreamConfig from "./../../hooks/useStreamConfig";
import AlertToast from "./AlertToast";
import {
  DEFAULT_ALERT_TEMPLATES,
  renderAlertMessage,
} from "./../../utils/alerts";
import { setMuted } from "./../../utils/alertSounds";

export default function AlertQueue({ onDebug }) {
  const { pending, debug } = useAlerts();
  const streamConfig = useStreamConfig();

  // Push global mute state into the sound module any time it changes
  useEffect(() => {
    setMuted(!!streamConfig.alertSoundsMuted);
  }, [streamConfig.alertSoundsMuted]);

  // Bubble debug info up to the page so /overlay/alerts?debug can render it
  useEffect(() => {
    if (onDebug) onDebug({ ...debug, pendingCount: pending.length });
  }, [debug.authed, debug.listening, debug.lastError, debug.totalDocs, pending.length, onDebug]); // eslint-disable-line react-hooks/exhaustive-deps

  const [templates, setTemplates] = useState(DEFAULT_ALERT_TEMPLATES);
  // Local display queue — alerts are snapshotted here the moment they appear
  // in `pending`. Immune to TTL expiry. Processed FIFO, one toast at a time.
  const [displayQueue, setDisplayQueue] = useState([]);
  const [activeAlert, setActiveAlert]   = useState(null);
  // Track ids we've already queued (prevents re-queueing the same alert if
  // the Firestore snapshot briefly includes it before consumed=true lands).
  const seenRef = useRef(new Set());

  // Live alert templates
  useEffect(() => {
    const unsub = onSnapshot(
      doc(db, "config", "alertTemplates"),
      (snap) => {
        if (snap.exists()) {
          setTemplates({ ...DEFAULT_ALERT_TEMPLATES, ...snap.data() });
        }
      },
      () => {}
    );
    return unsub;
  }, []);

  // Snapshot new alerts into the local display queue as they arrive.
  // Each alert is copied once — if it later expires from `pending`, the
  // local copy in displayQueue still fires.
  useEffect(() => {
    let added = false;
    const newAlerts = [];
    for (const a of pending) {
      if (!seenRef.current.has(a.id)) {
        seenRef.current.add(a.id);
        newAlerts.push({ ...a }); // shallow copy — snapshot
        added = true;
      }
    }
    if (added) {
      setDisplayQueue((q) => [...q, ...newAlerts]);
    }
  }, [pending]);

  // Advance: when nothing is active and the queue has items, pop the next one.
  useEffect(() => {
    if (activeAlert) return;
    if (displayQueue.length === 0) return;
    const [next, ...rest] = displayQueue;
    setActiveAlert(next);
    setDisplayQueue(rest);
  }, [activeAlert, displayQueue]);

  async function handleComplete() {
    const alert = activeAlert;
    setActiveAlert(null);
    if (!alert) return;
    try {
      await updateDoc(doc(db, "alerts", alert.id), { consumed: true });
    } catch {
      // Permission or network error — fall through; seenRef prevents re-queue.
    }
  }

  if (!activeAlert) return null;

  // Resolve display message: explicit override > template render > raw type
  const message =
    activeAlert.message ||
    renderAlertMessage(templates[activeAlert.type], activeAlert.fields || {}) ||
    activeAlert.type;

  return (
    <AlertToast
      key={activeAlert.id}
      alert={activeAlert}
      message={message}
      onComplete={handleComplete}
    />
  );
}
